import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
// utils
import localStorageAvailable from '../utils/minimal/localStorageAvailable';
// components
import { useSettingsContext } from '../components/minimal/settings';
//
import { allLangs, defaultLang } from './config-lang';
import { getI18nLocalStorageValue, getI18nLocalStorageKey } from './utils';
// Switching goes through this rather than i18n.changeLanguage: only the active language's
// bundle is loaded (#1100), so the target one has to be fetched before we switch to it.
import { changeLanguage } from './i18n';

// ----------------------------------------------------------------------

export default function useLocales() {
  const { i18n, t: translate } = useTranslation();

  const { onChangeDirectionByLang } = useSettingsContext();

  const storageAvailable = localStorageAvailable();

  // Check for lang parameter in URL on each render
  const urlParams = new URLSearchParams(window.location.search);
  const langFromUrl = urlParams.get('lang');

  // Handle URL parameter changes dynamically
  useEffect(() => {
    if (langFromUrl && ['en', 'he'].includes(langFromUrl) && i18n.language !== langFromUrl) {
      // Commit direction and storage only once the bundle is in and this switch is still the
      // current one — otherwise the layout flips to a language whose strings have not arrived.
      changeLanguage(langFromUrl).then((applied) => {
        if (!applied) return;
        onChangeDirectionByLang(langFromUrl);
        // Save the language from URL to localStorage for future visits
        if (storageAvailable) {
          localStorage.setItem(getI18nLocalStorageKey(), langFromUrl);
        }
      }).catch((error) => {
        console.error('Failed to load language bundle', langFromUrl, error);
      });
    }
  }, [langFromUrl, i18n, onChangeDirectionByLang, storageAvailable]);

  const langStorage = storageAvailable ? getI18nLocalStorageValue() : '';

  const currentLang = allLangs.find((_lang) => _lang.value === langStorage) || defaultLang;

  const handleChangeLanguage = async (newlang) => {
    try {
      // Await the bundle before flipping direction/storage so text and layout change together,
      // and so a language whose bundle failed to load is never persisted.
      const applied = await changeLanguage(newlang);
      if (!applied) return;

      onChangeDirectionByLang(newlang);
      if (storageAvailable) {
        localStorage.setItem(getI18nLocalStorageKey(), newlang);
      }
    } catch (error) {
      console.error('Failed to load language bundle', newlang, error);
    }
  };

  return {
    onChangeLang: handleChangeLanguage,
    // Return i18next `t` directly — it's stable per i18n instance and re-renders on language change.
    // Wrapping it in an arrow would create a fresh identity every render and break `useCallback` deps.
    translate,
    currentLang,
    allLangs,
  };
}
